import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useAuth } from "@/lib/auth-context";
import {
  getStoredReferralCode,
  clearStoredReferralCode,
  lookupAffiliateByCode,
  confirmReferral,
  type AffiliatePublicProfile,
} from "@/lib/referral";
import { API_BASE } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";

export function ReferralPage() {
  const { user, pendingReferral, setPendingReferral } = useAuth();
  const navigate = useNavigate();

  const [storedCode] = useState(() => getStoredReferralCode());
  const [affiliate, setAffiliate] = useState<AffiliatePublicProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [codeInput, setCodeInput] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Redirect if referral already handled
  useEffect(() => {
    if (pendingReferral === false) {
      navigate("/", { replace: true });
    }
  }, [pendingReferral, navigate]);

  // Lookup affiliate if we have a stored code
  useEffect(() => {
    if (!storedCode) {
      setLoading(false);
      return;
    }
    lookupAffiliateByCode(storedCode).then((profile) => {
      setAffiliate(profile);
      setLoading(false);
    });
  }, [storedCode]);

  if (!user || pendingReferral === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Skeleton className="h-10 w-10 rounded-full" />
      </div>
    );
  }

  async function handleConfirm(code: string | null) {
    setSubmitting(true);
    setError(null);
    try {
      await confirmReferral(code);
      clearStoredReferralCode();
      setPendingReferral(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
      setSubmitting(false);
    }
  }

  async function handleCodeSubmit(e: React.FormEvent) {
    e.preventDefault();
    const code = codeInput.trim();
    if (!code) return;

    setSubmitting(true);
    setError(null);

    // First validate the code exists
    const profile = lookupAffiliateByCode(code);
    const result = await profile;
    if (!result) {
      setError("Invalid referral code. Please check and try again.");
      setSubmitting(false);
      return;
    }

    // Show the affiliate profile briefly, then confirm
    setAffiliate(result);
    try {
      await confirmReferral(code);
      clearStoredReferralCode();
      setPendingReferral(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Skeleton className="h-16 w-16 rounded-full" />
          <Skeleton className="h-4 w-48" />
        </div>
      </div>
    );
  }

  // Has a stored code and found the affiliate
  if (storedCode && affiliate) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="w-full max-w-sm space-y-6 text-center">
          <h1 className="text-2xl font-semibold text-foreground">Welcome!</h1>
          <p className="text-muted-foreground">You were referred by</p>

          <div className="flex flex-col items-center gap-3 rounded-lg border border-border bg-card p-6">
            {affiliate.photoUrl && (
              <img
                src={affiliate.photoUrl.startsWith("/") ? `${API_BASE}${affiliate.photoUrl}` : affiliate.photoUrl}
                alt={affiliate.name}
                className="h-16 w-16 rounded-full object-cover"
              />
            )}
            <p className="text-lg font-medium text-foreground">{affiliate.name}</p>
            {affiliate.bio && (
              <p className="text-sm text-muted-foreground line-clamp-2">{affiliate.bio}</p>
            )}
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <button
            onClick={() => handleConfirm(storedCode)}
            disabled={submitting}
            className="w-full rounded-md bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {submitting ? "Confirming..." : "Continue"}
          </button>

        </div>
      </div>
    );
  }

  // No stored code — show input form
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm space-y-6 text-center">
        <h1 className="text-2xl font-semibold text-foreground">Welcome!</h1>
        <p className="text-muted-foreground">Do you have a referral code?</p>

        <form onSubmit={handleCodeSubmit} className="space-y-4">
          <input
            type="text"
            value={codeInput}
            onChange={(e) => {
              setCodeInput(e.target.value);
              setError(null);
            }}
            placeholder="Enter referral code"
            className="w-full rounded-md border border-border bg-card px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            disabled={submitting}
          />

          {error && <p className="text-sm text-destructive">{error}</p>}

          <button
            type="submit"
            disabled={submitting || !codeInput.trim()}
            className="w-full rounded-md bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {submitting ? "Confirming..." : "Continue"}
          </button>
        </form>

        <button
          onClick={() => handleConfirm(null)}
          disabled={submitting}
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          Skip — I don't have a code
        </button>
      </div>
    </div>
  );
}
